import { useFormik } from 'formik'
import React, { ChangeEvent, useState } from 'react'
import ModalComponent from '..'
import { toAudiencePayload } from '../../../helpers/audience-payload'
import { useModalControl } from '../../../hooks/useModalControl'
import { ListCategoryDoc } from '../../../redux-toolkits/categories/categories.type'
import {
  useCreateSubCategoryMutation,
  useEditSubCategoryMutation,
  useGetSingleCategoryQuery,
} from '../../../redux-toolkits/products/products.slice'
import {
  CreateSubCategory,
  SingleSubCategory,
} from '../../../redux-toolkits/products/products.type'
import { CreateSubCategorySchema } from '../../../validation/validate'
import { Button } from '../../button'
import { ImageUploadButton } from '../../button/imageUploadButton'
import Icon from '../../icon'
import { ImageBanner } from '../../images/imageBanner'
import Dropdown from '../../inputs/dropdown'
import { EditInput } from '../../inputs/editInput'
import { TextInput } from '../../inputs/textInput'
import { AudienceFields } from '../../product/audience-fields'
import { getCategorySectionLabel } from '../../product/category-section'
import { ImageUploadPreviewModal } from './ImagePreviewModal'
import { SelectSubCategoriesModal } from './selectSubCategoriesModal'

const statuses = [
  { text: 'Active', value: true },
  { text: 'Inactive', value: false },
]

const getSubCategoryInitialValues = (
  categoryId: string,
  subCategory?: SingleSubCategory,
) => ({
  icon: subCategory?.icon ?? '',
  name: subCategory?.name ?? '',
  description: subCategory?.description ?? '',
  categoryId: categoryId ?? '',
  parentSubcategory: subCategory?.parentSubcategory ?? undefined,
  isActive: subCategory?.isActive ?? true,
  ageRange: subCategory?.ageRange ?? null,
  genders: subCategory?.genders ?? [],
})

type SubCategoryFormValues = ReturnType<typeof getSubCategoryInitialValues>

const toSubCategoryPayload = (
  values: SubCategoryFormValues,
): CreateSubCategory => ({
  name: values.name,
  description: values.description,
  icon: values.icon,
  categoryId: values.categoryId,
  isActive: values.isActive,
  parentSubcategory: values.parentSubcategory?._id ?? null,
  ourPicks: undefined,
  ...toAudiencePayload(values),
})

const ParentCategorySection = ({
  category,
}: {
  category?: Pick<ListCategoryDoc, 'name' | 'section'>
}) => (
  <p className="w-full text-sm text-sec-black">
    Section:{' '}
    <span className="font-medium text-black">
      {getCategorySectionLabel(category?.section)}
    </span>
    {category?.name ? ` (from ${category.name})` : ''}
  </p>
)

export const AddSubCategoryModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (data: SingleSubCategory) => void
  mode: 'edit' | 'create'
  subCategory?: SingleSubCategory
  categoryId: string
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  mode,
  subCategory,
  categoryId,
}) => {
  const [uploadedIcon, setUploadedIcon] = useState<File>()
  const [editIndex, setEditIndex] = useState<number>()

  const { modal, modalOpen, setModalOpen, handleModalOpen, handleModalClose } =
    useModalControl()

  const [createSubCategory, { isLoading: creating }] =
    useCreateSubCategoryMutation()

  const [editSubCategory, { isLoading: editing }] = useEditSubCategoryMutation()

  const { data: parentCategory } = useGetSingleCategoryQuery(
    { categoryId },
    { skip: !open || !categoryId },
  )

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: getSubCategoryInitialValues(categoryId, subCategory),
    enableReinitialize: true,
    validationSchema: CreateSubCategorySchema,
    onSubmit: async (values) => {
      const payload = toSubCategoryPayload(values)

      if (mode === 'create') {
        const resp = await createSubCategory(payload)

        if (resp.data?.success) {
          resetForm()
          handleModalClose()
          onSuccessCallback(resp.data.data)
        }
      } else if (mode === 'edit' && subCategory) {
        const resp = await editSubCategory({
          id: subCategory._id,
          payload: payload,
        })

        if (resp.data?.success) {
          resetForm()
          handleModalClose()
          onSuccessCallback(resp.data.data)
        }
      }
    },
  })

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return

    const selectedFile = event.target.files[0]

    setUploadedIcon(selectedFile)

    handleModalOpen('imageUploadPreviewModal')
  }

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        onCloseCallback()
      }}
      title={mode === 'create' ? 'Add subcategory' : 'Manage subcategory'}
      fullScreen={false}
      childrenClassName="md:!max-w-[420px]"
    >
      <form
        className="flex items-center justify-center flex-col gap-4"
        onSubmit={handleSubmit}
      >
        {mode === 'create' && (
          <>
            {values.icon ? (
              <ImageBanner
                imageUrl={values.icon}
                loading={false}
                className="
                        !rounded-full
                        !w-[120px] !max-h-[120px]
                        !bg-contain
                        "
                errors={errors.icon ? errors.icon : ''}
                onImageEdit={(e) => {
                  handleFileChange(e)
                }}
              />
            ) : (
              <ImageUploadButton
                handleChange={(e) => {
                  handleFileChange(e)
                }}
                showText
                icon={'pic-icon'}
                iconDim={52}
                text={`Upload subcategory svg`}
                className="
                !relative !w-full
                !m-0 !top-0 !right-0
                !h-fit
                !rounded-lg
                !col-span-full
                "
              />
            )}
          </>
        )}

        <EditInput
          title="Subcategory name"
          value={values.name}
          onChange={handleChange}
          type="text"
          placeholder="Subcategory name"
          onEditSave={() => {
            setEditIndex(undefined)
          }}
          editing={editIndex === 1}
          onEditClick={() => {
            setEditIndex(1)
          }}
          onCancel={() => {
            if (mode === 'create') {
              setFieldValue('name', '')
            } else {
              setFieldValue('name', subCategory?.name)
            }
            setEditIndex(undefined)
          }}
          loading={false}
          inputName="name"
          mode={mode === 'create' ? 'edit' : 'edit'}
        />

        <EditInput
          title="Subcategory description"
          value={values.description}
          onChange={handleChange}
          type="textarea"
          placeholder="Subcategory description"
          onEditSave={() => {
            setEditIndex(undefined)
          }}
          editing={editIndex === 2}
          onEditClick={() => {
            setEditIndex(2)
          }}
          onCancel={() => {
            if (mode === 'create') {
              setFieldValue('description', '')
            } else {
              setFieldValue('description', subCategory?.description)
            }
            setEditIndex(undefined)
          }}
          loading={false}
          inputName="description"
          mode={mode === 'create' ? 'edit' : 'edit'}
        />

        <EditInput
          title="Parent Subcategory"
          value={values?.parentSubcategory?.name ?? ''}
          onChange={handleChange}
          type="text"
          placeholder="Parent subcategory"
          onEditSave={() => {}}
          editing={false}
          onEditClick={() => {
            handleModalOpen('selectParentSubCategoryModal')
          }}
          onCancel={() => {}}
          loading={false}
          inputName=""
        />

        <ParentCategorySection
          category={parentCategory?.data ?? subCategory?.category}
        />

        <div className="w-full">
          <AudienceFields
            contentLabel="subcategory"
            ageRange={values.ageRange}
            genders={values.genders}
            onAgeChange={(range) => {
              void setFieldValue('ageRange', range)
            }}
            onGendersChange={(genders) => {
              void setFieldValue('genders', genders)
            }}
          />
        </div>

        <Dropdown
          className="w-full"
          menuClassName="!max-h-[200px]"
          position="bottom start"
          menuButton={
            <TextInput
              type="text"
              title="Status"
              name="status"
              placeholder="Select status"
              value={values.isActive ? 'Active' : 'Inactive'}
              onBlur={handleBlur}
              onChange={handleChange}
              hasIcon={false}
              readOnly
              extra={
                <Icon
                  id={'left-caret'}
                  width={16}
                  height={16}
                  className="-rotate-90 text-sec-black"
                />
              }
            />
          }
          menuItems={statuses.map((status) => {
            return {
              name: status.text,
              value: status.value,
              onClick: () => {
                setFieldValue('isActive', status.value)
              },
            }
          })}
        />

        <Button
          label="Save"
          type="submit"
          className="mt-2 w-full"
          disabled={!isValid || creating || editing}
          loading={creating || editing}
          primary
        />
      </form>

      {uploadedIcon ? (
        <ImageUploadPreviewModal
          image={uploadedIcon as File}
          open={modal === 'imageUploadPreviewModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            setUploadedIcon(undefined)
          }}
          onSuccessCallback={(url) => {
            setFieldValue('icon', url)
          }}
        />
      ) : null}

      <SelectSubCategoriesModal
        open={modalOpen && modal === 'selectParentSubCategoryModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={(subCategory) => {
          setFieldValue('parentSubcategory', subCategory[0])
          handleModalClose()
        }}
        selectedSubCategories={
          subCategory?.parentSubcategory ? [subCategory.parentSubcategory] : []
        }
        categories={categoryId ? [categoryId] : []}
        maxSelection={1}
      />
    </ModalComponent>
  )
}
